/* OFF ROAD Watch -- Search Modal Web Component
   Triggered by any [data-orw-search-open] element on any page.
   Uses Shopify Predictive Search API for live results.
*/
(function () {
  'use strict';

  const DEBOUNCE_MS = 250;
  const RESULTS_LIMIT = 10;
  const SEARCH_ENDPOINT = '/search/suggest.json';
  const MONEY_FORMAT = {
    currency: 'INR',
    locale: 'en-IN',
    fractionDigits: 0,
  };

  class ORWSearch extends HTMLElement {
    constructor() {
      super();
      this._debounceTimer = null;
      this._abortController = null;
      this._isOpen = false;
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-orw-search-panel]');
      this.backdrop = this.querySelector('[data-orw-search-backdrop]');
      this.closeBtn = this.querySelector('[data-orw-search-close]');
      this.form = this.querySelector('[data-orw-search-form]');
      this.input = this.querySelector('[data-orw-search-input]');
      this.content = this.querySelector('[data-orw-search-content]');
      this.results = this.querySelector('[data-orw-search-results]');
      this.resultsGrid = this.querySelector('[data-orw-search-results-grid]');
      this.resultsHeading = this.querySelector('[data-orw-search-results-heading]');
      this.noMatch = this.querySelector('[data-orw-search-no-match]');
      this.viewAll = this.querySelector('[data-orw-search-view-all]');

      if (!this.panel || !this.input) return;

      this._bindEvents();
    }

    _bindEvents() {
      // Delegated trigger listener (works for any [data-orw-search-open] on any page)
      this._onDocClick = (e) => {
        const trigger = e.target && e.target.closest && e.target.closest('[data-orw-search-open]');
        if (trigger) {
          e.preventDefault();
          this.open();
        }
      };
      document.addEventListener('click', this._onDocClick);

      this._onKeydown = (e) => {
        if (e.key === 'Escape' && this._isOpen) {
          this.close();
        }
      };
      document.addEventListener('keydown', this._onKeydown);

      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());

      this.input.addEventListener('input', () => this._handleInput());

      // Enter opens the full ORW results page (/search). Empty query stays put.
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          const q = (this.input.value || '').trim();
          if (!q) {
            e.preventDefault();
            return;
          }
          this._softClose();
        });
      }
      if (this.viewAll) this.viewAll.addEventListener('click', () => this._softClose());

      // Click on a result card should close modal (so page navigation feels responsive)
      if (this.resultsGrid) {
        this.resultsGrid.addEventListener('click', (e) => {
          const a = e.target.closest('a');
          if (a) this._softClose();
        });
      }
      const staticLinks = this.querySelectorAll('[data-orw-search-content] a');
      staticLinks.forEach((a) => a.addEventListener('click', () => this._softClose()));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
    }

    open() {
      if (this._isOpen) return;
      this._isOpen = true;
      this.hidden = false;
      document.body.classList.add('orw-search-is-open');
      // Force reflow so transition fires
      void this.panel.offsetWidth;
      // Focus inside the same tap/click: iOS Safari only opens the keyboard
      // for focus() called during the user gesture, and typing starts at once.
      if (this.input) this.input.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        this.classList.add('orw-search--open');
        if (this.input && document.activeElement !== this.input) {
          this.input.focus({ preventScroll: true });
        }
      });
    }

    close() {
      if (!this._isOpen) return;
      this.classList.remove('orw-search--open');
      this._isOpen = false;

      // Reset after transition
      setTimeout(() => {
        this.hidden = true;
        document.body.classList.remove('orw-search-is-open');
        if (this.input) this.input.value = '';
        this._showDefault();
      }, 320);
    }

    _softClose() {
      // Visual close without reset (links are navigating; don't wipe state)
      this.classList.remove('orw-search--open');
      this._isOpen = false;
      setTimeout(() => {
        this.hidden = true;
        document.body.classList.remove('orw-search-is-open');
      }, 320);
    }

    _handleInput() {
      const q = (this.input.value || '').trim();
      clearTimeout(this._debounceTimer);

      if (!q) {
        this._showDefault();
        return;
      }

      this._debounceTimer = setTimeout(() => this._fetchResults(q), DEBOUNCE_MS);
    }

    async _fetchResults(q) {
      // Cancel any in-flight request
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();

      const params = new URLSearchParams({
        q,
        'resources[type]': 'product',
        'resources[limit]': String(RESULTS_LIMIT),
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,vendor,product_type,tag',
      });

      const url = `${SEARCH_ENDPOINT}?${params.toString()}`;

      try {
        const res = await fetch(url, { signal: this._abortController.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        this._renderResults(q, products);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[orw-search] predictive search failed:', err);
      }
    }

    _renderResults(q, products) {
      if (!this.results || !this.resultsGrid) return;

      if (this.resultsHeading) {
        this.resultsHeading.textContent = `Results for "${q}"`;
      }

      if (this.viewAll) {
        const params = new URLSearchParams({ q, type: 'product', 'options[prefix]': 'last', 'options[unavailable_products]': 'last' });
        this.viewAll.href = `${this.form ? this.form.getAttribute('action') : '/search'}?${params.toString()}`;
        this.viewAll.hidden = !products.length;
      }

      if (this.content) this.content.hidden = true;
      this.results.hidden = false;

      if (!products.length) {
        this.resultsGrid.innerHTML = '';
        if (this.noMatch) this.noMatch.hidden = false;
        return;
      }

      if (this.noMatch) this.noMatch.hidden = true;
      this.resultsGrid.innerHTML = products.map((p) => this._cardHTML(p)).join('');
    }

    _showDefault() {
      if (this.content) this.content.hidden = false;
      if (this.results) this.results.hidden = true;
      if (this.noMatch) this.noMatch.hidden = true;
      if (this.resultsGrid) this.resultsGrid.innerHTML = '';
      if (this.viewAll) this.viewAll.hidden = true;
    }

    _cardHTML(p) {
      /* Match the regular orw-product-card snippet UI:
         - Brand line: uppercased collection prefix from the product title
                       (e.g., "Terrain - ORQ..." -> "TERRAIN").
         - Title: SKU only (everything after "Collection - " prefix).
         Fallback for titles without " - " separator: split on first space. */
      const rawTitle = (p.title || '').trim();
      let brandPart, skuPart;
      const dashIdx = rawTitle.indexOf(' - ');
      if (dashIdx !== -1) {
        brandPart = rawTitle.slice(0, dashIdx).trim();
        skuPart = rawTitle.slice(dashIdx + 3).trim();
      } else {
        const firstSpace = rawTitle.indexOf(' ');
        if (firstSpace !== -1) {
          brandPart = rawTitle.slice(0, firstSpace).trim();
          skuPart = rawTitle.slice(firstSpace + 1).trim();
        } else {
          brandPart = '';
          skuPart = rawTitle;
        }
      }
      const brand = this._escape(brandPart.toUpperCase());
      const title = this._escape(skuPart);
      const url = p.url || '#';
      const image = (p.featured_image && p.featured_image.url) || p.image || '';
      const price = p.price || p.price_min || 0;
      const compareAt = p.compare_at_price || p.compare_at_price_min || null;
      const tags = Array.isArray(p.tags) ? p.tags.map((t) => String(t).toLowerCase()) : [];

      const priceNum = this._toNumber(price);
      const compareNum = this._toNumber(compareAt);
      const onSale = compareNum && compareNum > priceNum;

      // Same badge order as the Liquid card: Sold Out > sale > Trending > New
      const soldOut = p.available === false;
      let badge = '';
      if (soldOut) {
        badge = `<span class="orw-product-card__badge orw-product-card__badge--sold-out">Sold Out</span>`;
      } else if (onSale) {
        const pct = Math.round((1 - priceNum / compareNum) * 100);
        badge = `<span class="orw-product-card__badge orw-product-card__badge--sale">-${pct}% OFF</span>`;
      } else if (tags.includes('trending')) {
        badge = `<span class="orw-product-card__badge">Trending</span>`;
      } else if (tags.includes('new')) {
        badge = `<span class="orw-product-card__badge">New</span>`;
      }

      const compareHtml = onSale
        ? `<span class="orw-product-card__compare-price">${this._formatMoney(compareNum)}</span>`
        : '';
      const priceClass = onSale ? 'orw-product-card__sale-price' : 'orw-product-card__regular-price';

      const imgHtml = image
        ? `<img src="${image}" alt="${title}" class="orw-product-card__image orw-product-card__image--front" loading="lazy">`
        : '';

      return `
        <a href="${url}" class="orw-product-card${soldOut ? ' orw-product-card--sold-out' : ''}" data-orw-card>
          <div class="orw-product-card__image-wrap">
            ${badge}
            <div class="orw-product-card__shadow"></div>
            ${imgHtml}
          </div>
          <div class="orw-product-card__info">
            <div class="orw-product-card__details">
              ${brand ? `<span class="orw-product-card__brand">${brand}</span>` : ''}
              <span class="orw-product-card__title">${title}</span>
            </div>
            <div class="orw-product-card__price">
              <span class="${priceClass}">${this._formatMoney(priceNum)}</span>
              ${compareHtml}
            </div>
          </div>
        </a>
      `;
    }

    _toNumber(v) {
      if (v == null) return 0;
      if (typeof v === 'number') return v;
      // Shopify predictive returns "1000.00" for price in dollars (not cents)
      return parseFloat(String(v).replace(/[^0-9.]/g, '')) || 0;
    }

    _formatMoney(amount) {
      /* Match the INR ₹ format used by the regular cards on the rest of
         the site (e.g., "₹17,900"). */
      const v = Number(amount) || 0;
      try {
        return `₹${v.toLocaleString(MONEY_FORMAT.locale, { maximumFractionDigits: MONEY_FORMAT.fractionDigits })}`;
      } catch (_) {
        return `₹${v.toFixed(0)}`;
      }
    }

    _escape(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get('orw-search')) {
    customElements.define('orw-search', ORWSearch);
  }
})();
